import React, { useState, useEffect, useCallback } from 'react';
import {
  CurrencyDollarIcon,
  UserGroupIcon,
  GiftIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  BanknotesIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import apiClient from '../../utils/apiClient';

// ─── Sub-tab navigation ──────────────────────────────────────────

const SUB_TABS = [
  { id: 'overview', label: 'Overview', icon: CurrencyDollarIcon },
  { id: 'purchases', label: 'Purchases', icon: BanknotesIcon },
  { id: 'expenses', label: 'Expenses', icon: BanknotesIcon },
  { id: 'sales-team', label: 'Sales Team', icon: UserGroupIcon },
];

// ─── OVERVIEW SUB-TAB ────────────────────────────────────────────

const FinanceOverview = ({ summary, loading, onRefresh }) => {
  if (loading && !summary) {
    return <div className="text-center py-10 text-gray-400">Loading financial summary...</div>;
  }
  if (!summary) return null;

  const { profit, settlement, revenue_over_time } = summary;

  return (
    <div className="space-y-6">
      {/* Both Partners Overview */}
      {profit && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatCard title="Total Revenue" value={`${profit.total_revenue.toFixed(2)} JOD`} icon={CurrencyDollarIcon} color="emerald" />
          <StatCard title="Total Expenses" value={`${profit.total_expenses.toFixed(2)} JOD`} icon={BanknotesIcon} color="red" />
          <StatCard title="Total Profit" value={`${(profit.my_profit + profit.rashid_profit).toFixed(2)} JOD`} subtitle="After expenses & commissions" icon={ChartBarIcon} color="green" />
        </div>
      )}

      {/* Settlement */}
      {settlement && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard title="Ali Owes" value={`${settlement.ali_owes.toFixed(2)} JOD`} subtitle="To Rashid" icon={ArrowTrendingUpIcon} color="red" />
          <StatCard title="Rashid Owes" value={`${settlement.rashid_owes.toFixed(2)} JOD`} subtitle="To Ali" icon={ArrowTrendingUpIcon} color="amber" />
          <StatCard 
            title={settlement.direction === 'rashid_pays_ali' ? 'Rashid Pays Ali' : 'Ali Pays Rashid'} 
            value={`${settlement.net_settlement.toFixed(2)} JOD`} 
            subtitle="Net settlement" 
            icon={BanknotesIcon}
            color={settlement.direction === 'rashid_pays_ali' ? 'green' : 'red'} 
          />
          <StatCard title="Sales Commission" value={`${settlement.sales_commission.toFixed(2)} JOD`} subtitle="Both pay 50/50" icon={UserGroupIcon} color="purple" />
        </div>
      )}

      {/* Financial Overview Graph */}
      {revenue_over_time && revenue_over_time.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Financial Overview</h3>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenue_over_time}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="accumulated_revenue" 
                  stroke="#10B981" 
                  strokeWidth={1.5} 
                  strokeOpacity={0.4}
                  dot={false}
                  name="Accumulated Revenue"
                />
                <Line 
                  type="monotone" 
                  dataKey="accumulated_expenses" 
                  stroke="#EF4444" 
                  strokeWidth={1.5} 
                  strokeOpacity={0.4}
                  dot={false}
                  name="Accumulated Expenses"
                />
                <Line 
                  type="monotone" 
                  dataKey="profit" 
                  stroke="#3B82F6" 
                  strokeWidth={3} 
                  dot={{ fill: '#3B82F6', r: 3 }}
                  name="Profit"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── PURCHASES SUB-TAB ───────────────────────────────────────────

const PurchasesList = ({ salesPersons }) => {
  const [purchases, setPurchases] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPartner, setFilterPartner] = useState('');
  const [filterSettled, setFilterSettled] = useState('');
  const [filterFree, setFilterFree] = useState('');
  const [filterSP, setFilterSP] = useState('');
  const [editingId, setEditingId] = useState(null);

  const fetchPurchases = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/api/auth/admin/finance/purchases/?page=${page}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (filterPartner) url += `&partner=${filterPartner}`;
      if (filterSettled) url += `&settled=${filterSettled}`;
      if (filterFree) url += `&free=${filterFree}`;
      if (filterSP) url += `&sales_person=${filterSP}`;
      const data = await apiClient.get(url);
      setPurchases(data.purchases || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch purchases:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterPartner, filterSettled, filterFree, filterSP]);

  useEffect(() => { fetchPurchases(); }, [fetchPurchases]);

  const updatePurchase = async (id, updates) => {
    try {
      await apiClient.patch(`/api/auth/admin/finance/purchases/${id}/`, updates);
      setPurchases(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
      setEditingId(null);
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const handleBulkSettle = async (partner) => {
    if (!window.confirm(`Settle ALL unsettled purchases for ${partner === 'me' ? 'My' : "Rashid's"} account?`)) return;
    try {
      await apiClient.post('/api/auth/admin/finance/bulk-settle/', { partner });
      fetchPurchases();
    } catch (err) {
      console.error('Failed to bulk settle:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text" placeholder="Search email, name, course..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
        <select value={filterPartner} onChange={e => { setFilterPartner(e.target.value); setPage(1); }}
          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
          <option value="">All Accounts</option>
          <option value="me">My Account</option>
          <option value="rashid">Rashid</option>
        </select>
        <select value={filterSettled} onChange={e => { setFilterSettled(e.target.value); setPage(1); }}
          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
          <option value="">All Status</option>
          <option value="true">Settled</option>
          <option value="false">Unsettled</option>
        </select>
        <select value={filterFree} onChange={e => { setFilterFree(e.target.value); setPage(1); }}
          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
          <option value="">All Types</option>
          <option value="true">Free Trial</option>
          <option value="false">Paid</option>
        </select>
        <select value={filterSP} onChange={e => { setFilterSP(e.target.value); setPage(1); }}
          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
          <option value="">All Sales</option>
          {(salesPersons || []).map(sp => (
            <option key={sp.id} value={sp.id}>{sp.name}</option>
          ))}
        </select>
      </div>

      {/* Bulk Actions */}
      <div className="flex gap-2">
        <button onClick={() => handleBulkSettle('me')}
          className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Settle All Mine
        </button>
        <button onClick={() => handleBulkSettle('rashid')}
          className="px-3 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          Settle All Rashid's
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700/50">
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">User</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Subscription</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Amount</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Date</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Account</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Settled</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Free</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Sales</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr><td colSpan={9} className="text-center py-6 text-gray-400">Loading...</td></tr>
            ) : purchases.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-6 text-gray-400">No purchases found</td></tr>
            ) : purchases.map(p => (
              <PurchaseRow key={p.id} purchase={p} salesPersons={salesPersons}
                isEditing={editingId === p.id}
                onEdit={() => setEditingId(p.id)}
                onSave={(updates) => updatePurchase(p.id, updates)}
                onCancel={() => setEditingId(null)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{total} purchases</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
              className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-30">
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="text-sm px-3 text-gray-600 dark:text-gray-300">{page}/{pages}</span>
            <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages}
              className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-30">
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const PurchaseRow = ({ purchase: p, salesPersons, isEditing, onEdit, onSave, onCancel }) => {
  const [partner, setPartner] = useState(p.partner_account || '');
  const [settled, setSettled] = useState(p.is_settled);
  const [free, setFree] = useState(p.is_free_trial);
  const [spId, setSpId] = useState(p.sales_person_id || '');
  const [note, setNote] = useState(p.financial_note || '');
  const [amount, setAmount] = useState(p.amount_paid);

  // Auto-zero amount when free trial is checked
  const handleFreeChange = (checked) => {
    setFree(checked);
    if (checked) {
      setAmount(0);
    }
  };

  if (!isEditing) {
    return (
      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer" onClick={onEdit}>
        <td className="px-3 py-2">
          <button 
            onClick={() => window.location.href = `/admin/stats?tab=users&userId=${p.user_id}`}
            className="text-left hover:underline"
          >
            <div className="text-gray-900 dark:text-white text-xs font-medium truncate max-w-[140px]">{p.user_email}</div>
            <div className="text-gray-400 text-xs truncate max-w-[140px]">{p.user_name}</div>
          </button>
        </td>
        <td className="px-3 py-2">
          <div 
            className="text-xs text-gray-600 dark:text-gray-300 max-w-[150px] truncate cursor-help"
            title={`${p.course_name} - ${p.tier_name}${p.game_extras?.length > 0 ? '\nGame Extras: ' + p.game_extras.join(', ') : ''}`}
          >
            {p.subscription_summary}
          </div>
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-gray-900 dark:text-white">{p.amount_paid.toFixed(2)} JOD</td>
        <td className="px-3 py-2 text-xs text-gray-500">{new Date(p.purchased_at).toLocaleDateString()}</td>
        <td className="px-3 py-2">
          {p.partner_account ? (
            <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
              p.partner_account === 'me' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
            }`}>{p.partner_account === 'me' ? 'Me' : 'Rashid'}</span>
          ) : (
            <span className="text-xs text-amber-500">—</span>
          )}
        </td>
        <td className="px-3 py-2">
          {p.is_settled
            ? <CheckCircleIcon className="h-4 w-4 text-green-500" />
            : <XCircleIcon className="h-4 w-4 text-red-400" />}
        </td>
        <td className="px-3 py-2">
          {p.is_free_trial && <GiftIcon className="h-4 w-4 text-green-500" />}
        </td>
        <td className="px-3 py-2 text-xs text-gray-500 truncate max-w-[80px]">{p.sales_person_name || '—'}</td>
        <td className="px-3 py-2 text-xs text-gray-400 truncate max-w-[100px]">{p.financial_note || ''}</td>
      </tr>
    );
  }

  return (
    <tr className="bg-blue-50 dark:bg-blue-900/10">
      <td className="px-3 py-2">
        <button 
          onClick={(e) => { e.stopPropagation(); window.location.href = `/admin/stats?tab=users&userId=${p.user_id}`; }}
          className="text-left hover:underline"
        >
          <div className="text-gray-900 dark:text-white text-xs font-medium">{p.user_email}</div>
        </button>
      </td>
      <td className="px-3 py-2">
        <div 
          className="text-xs text-gray-600 dark:text-gray-300 cursor-help"
          title={`${p.course_name} - ${p.tier_name}${p.game_extras?.length > 0 ? '\nGame Extras: ' + p.game_extras.join(', ') : ''}`}
        >
          {p.subscription_summary}
        </div>
      </td>
      <td className="px-3 py-1">
        <input type="number" step="0.01" value={amount} onChange={e => setAmount(parseFloat(e.target.value) || 0)}
          className="w-20 px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
      </td>
      <td className="px-3 py-2 text-xs">{new Date(p.purchased_at).toLocaleDateString()}</td>
      <td className="px-3 py-1">
        <select value={partner} onChange={e => setPartner(e.target.value)}
          className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
          <option value="">—</option>
          <option value="me">Me</option>
          <option value="rashid">Rashid</option>
        </select>
      </td>
      <td className="px-3 py-1">
        <input type="checkbox" checked={settled} onChange={e => setSettled(e.target.checked)}
          className="rounded border-gray-300 text-blue-600" />
      </td>
      <td className="px-3 py-1">
        <input type="checkbox" checked={free} onChange={e => handleFreeChange(e.target.checked)}
          className="rounded border-gray-300 text-green-600" />
      </td>
      <td className="px-3 py-1">
        <select value={spId} onChange={e => setSpId(e.target.value)}
          className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
          <option value="">None</option>
          {(salesPersons || []).map(sp => (
            <option key={sp.id} value={sp.id}>{sp.name} ({sp.commission_rate}%)</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-1">
        <div className="flex gap-1">
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note..."
            className="w-16 px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          <button onClick={() => onSave({
            partner_account: partner, is_settled: settled, is_free_trial: free,
            sales_person_id: spId || null, financial_note: note, amount_paid: amount
          })} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
          <button onClick={onCancel} className="px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded">Cancel</button>
        </div>
      </td>
    </tr>
  );
};

// ─── EXPENSES SUB-TAB ────────────────────────────────────────────

const ExpensesList = ({ onRefresh }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newExpense, setNewExpense] = useState({
    description: '', amount: '', paid_by: 'me', cost_bearer: 'both', category: '', note: '', expense_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { fetchExpenses(); }, []);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/auth/admin/finance/expenses/');
      setExpenses(data.expenses || []);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newExpense.description || !newExpense.amount) return;
    try {
      await apiClient.post('/api/auth/admin/finance/expenses/', newExpense);
      setShowAdd(false);
      setNewExpense({ description: '', amount: '', paid_by: 'me', cost_bearer: 'both', category: '', note: '', expense_date: new Date().toISOString().split('T')[0] });
      fetchExpenses();
      onRefresh();
    } catch (error) {
      console.error('Failed to add expense:', error);
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await apiClient.patch(`/api/auth/admin/finance/expenses/${id}/`, updates);
      setEditingId(null);
      fetchExpenses();
      onRefresh();
    } catch (error) {
      console.error('Failed to update expense:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await apiClient.delete(`/api/auth/admin/finance/expenses/${id}/`);
      fetchExpenses();
      onRefresh();
    } catch (error) {
      console.error('Failed to delete expense:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Expenses</h3>
        <button onClick={() => setShowAdd(!showAdd)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium flex items-center gap-2">
          <PlusIcon className="h-4 w-4" />
          Add Expense
        </button>
      </div>

      {showAdd && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input value={newExpense.description} onChange={e => setNewExpense({...newExpense, description: e.target.value})}
              placeholder="Description" className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
            <input type="number" step="0.01" value={newExpense.amount} onChange={e => setNewExpense({...newExpense, amount: e.target.value})}
              placeholder="Amount" className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
            <select value={newExpense.paid_by} onChange={e => setNewExpense({...newExpense, paid_by: e.target.value})}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
              <option value="me">Paid by Me</option>
              <option value="rashid">Paid by Rashid</option>
              <option value="both">Paid by Both</option>
            </select>
            <select value={newExpense.cost_bearer} onChange={e => setNewExpense({...newExpense, cost_bearer: e.target.value})}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
              <option value="both">Cost: Both (50/50)</option>
              <option value="me">Cost: Me</option>
              <option value="rashid">Cost: Rashid</option>
            </select>
            <input value={newExpense.category} onChange={e => setNewExpense({...newExpense, category: e.target.value})}
              placeholder="Category (optional)" className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
            <input type="date" value={newExpense.expense_date} onChange={e => setNewExpense({...newExpense, expense_date: e.target.value})}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
          </div>
          <textarea value={newExpense.note} onChange={e => setNewExpense({...newExpense, note: e.target.value})}
            placeholder="Note (optional)" rows="2" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
          <div className="flex gap-2">
            <button onClick={handleAdd} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">Add</button>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Paid By</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cost Bearer</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Settled</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div></td></tr>
            ) : expenses.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No expenses found</td></tr>
            ) : (
              expenses.map(exp => (
                <ExpenseRow key={exp.id} expense={exp} isEditing={editingId === exp.id}
                  onEdit={() => setEditingId(exp.id)} onSave={(updates) => handleUpdate(exp.id, updates)}
                  onCancel={() => setEditingId(null)} onDelete={() => handleDelete(exp.id)} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ExpenseRow = ({ expense: e, isEditing, onEdit, onSave, onCancel, onDelete }) => {
  const [description, setDescription] = useState(e.description);
  const [amount, setAmount] = useState(e.amount);
  const [paidBy, setPaidBy] = useState(e.paid_by);
  const [costBearer, setCostBearer] = useState(e.cost_bearer);
  const [settled, setSettled] = useState(e.is_settled);
  const [expenseDate, setExpenseDate] = useState(e.expense_date);

  if (!isEditing) {
    return (
      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
        <td className="px-4 py-3 text-gray-900 dark:text-white">{e.description}</td>
        <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">{e.amount.toFixed(2)} JOD</td>
        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 capitalize">{e.paid_by}</td>
        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 capitalize">{e.cost_bearer}</td>
        <td className="px-4 py-3">
          {e.is_settled ? <CheckCircleIcon className="h-4 w-4 text-green-500" /> : <XCircleIcon className="h-4 w-4 text-red-400" />}
        </td>
        <td className="px-4 py-3 text-gray-500">{new Date(e.expense_date).toLocaleDateString()}</td>
        <td className="px-4 py-3">
          <div className="flex gap-2">
            <button onClick={onEdit} className="text-xs text-blue-600 hover:text-blue-700">Edit</button>
            <button onClick={onDelete} className="text-xs text-red-600 hover:text-red-700">Delete</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-blue-50 dark:bg-blue-900/10">
      <td className="px-2 py-2">
        <input value={description} onChange={e => setDescription(e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
      </td>
      <td className="px-2 py-2">
        <input type="number" step="0.01" value={amount} onChange={e => setAmount(parseFloat(e.target.value))}
          className="w-24 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
      </td>
      <td className="px-2 py-2">
        <select value={paidBy} onChange={e => setPaidBy(e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="me">Me</option>
          <option value="rashid">Rashid</option>
          <option value="both">Both</option>
        </select>
      </td>
      <td className="px-2 py-2">
        <select value={costBearer} onChange={e => setCostBearer(e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="both">Both</option>
          <option value="me">Me</option>
          <option value="rashid">Rashid</option>
        </select>
      </td>
      <td className="px-2 py-2 text-center">
        <input type="checkbox" checked={settled} onChange={e => setSettled(e.target.checked)}
          className="rounded border-gray-300 text-green-600" />
      </td>
      <td className="px-2 py-2">
        <input type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
      </td>
      <td className="px-2 py-2">
        <div className="flex gap-1">
          <button onClick={() => onSave({ description, amount, paid_by: paidBy, cost_bearer: costBearer, is_settled: settled, expense_date: expenseDate })}
            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">Save</button>
          <button onClick={onCancel} className="px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded">Cancel</button>
        </div>
      </td>
    </tr>
  );
};

// ─── SALES TEAM SUB-TAB ──────────────────────────────────────────

const SalesTeam = ({ salesPersons, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRate, setNewRate] = useState('');
  const [payoutSP, setPayoutSP] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNote, setPayoutNote] = useState('');
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(null);

  const handleAdd = async () => {
    if (!newName.trim() || !newRate) return;
    try {
      await apiClient.post('/api/auth/admin/finance/sales-persons/', {
        name: newName.trim(), commission_rate: parseFloat(newRate)
      });
      setShowAdd(false);
      setNewName('');
      setNewRate('');
      onRefresh();
    } catch (err) {
      console.error('Failed to add:', err);
    }
  };

  const handlePayout = async () => {
    if (!payoutSP || !payoutAmount || parseFloat(payoutAmount) <= 0) return;
    try {
      await apiClient.post(`/api/auth/admin/finance/sales-persons/${payoutSP}/payout/`, {
        amount: parseFloat(payoutAmount), note: payoutNote
      });
      setPayoutSP(null);
      setPayoutAmount('');
      setPayoutNote('');
      onRefresh();
    } catch (err) {
      console.error('Failed to payout:', err);
    }
  };

  const loadHistory = async (spId) => {
    try {
      const data = await apiClient.get(`/api/auth/admin/finance/sales-persons/${spId}/payouts/`);
      setPayoutHistory(data);
      setShowHistory(spId);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const toggleActive = async (sp) => {
    try {
      await apiClient.patch(`/api/auth/admin/finance/sales-persons/${sp.id}/`, {
        is_active: !sp.is_active
      });
      onRefresh();
    } catch (err) {
      console.error('Failed to toggle:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Sales People</h3>
        <button onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <PlusIcon className="h-3.5 w-3.5" /> Add Person
        </button>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name"
            className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          <input value={newRate} onChange={e => setNewRate(e.target.value)} placeholder="Commission %" type="number" step="0.01"
            className="w-28 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          <button onClick={handleAdd} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Create</button>
          <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-sm bg-gray-300 dark:bg-gray-600 rounded-lg">Cancel</button>
        </div>
      )}

      {/* Sales Persons Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700/50">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Rate</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Revenue</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Commission</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Paid</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Owed</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Sales</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {(salesPersons || []).length === 0 ? (
              <tr><td colSpan={8} className="text-center py-6 text-gray-400">No sales people yet</td></tr>
            ) : (salesPersons || []).map(sp => (
              <tr key={sp.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${!sp.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">
                  {sp.name}
                  {!sp.is_active && <span className="ml-1 text-xs text-red-400">(inactive)</span>}
                </td>
                <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{sp.commission_rate}%</td>
                <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{sp.total_revenue.toFixed(2)} JOD</td>
                <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{sp.total_commission.toFixed(2)} JOD</td>
                <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{sp.total_paid_out.toFixed(2)} JOD</td>
                <td className={`px-4 py-2.5 font-bold ${sp.balance_owed > 0 ? 'text-red-500' : 'text-green-500'}`}>
                  {sp.balance_owed.toFixed(2)} JOD
                </td>
                <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{sp.purchase_count}</td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1">
                    {sp.balance_owed > 0 && (
                      <button onClick={() => { setPayoutSP(sp.id); setPayoutAmount(sp.balance_owed.toFixed(2)); }}
                        className="px-2 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700">Pay</button>
                    )}
                    <button onClick={() => loadHistory(sp.id)}
                      className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded">History</button>
                    <button onClick={() => toggleActive(sp)}
                      className={`px-2 py-0.5 text-xs rounded ${sp.is_active ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                      {sp.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payout Modal */}
      {payoutSP && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Record Payout</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600 dark:text-gray-400">Amount ($)</label>
                <input value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)} type="number" step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-600 dark:text-gray-400">Note (optional)</label>
                <input value={payoutNote} onChange={e => setPayoutNote(e.target.value)} placeholder="e.g. Bank transfer Feb 2026"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={handlePayout}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">Record Payout</button>
              <button onClick={() => { setPayoutSP(null); setPayoutAmount(''); setPayoutNote(''); }}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Payout History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payout History</h3>
              <button onClick={() => setShowHistory(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            {payoutHistory.length === 0 ? (
              <p className="text-gray-400 text-sm">No payouts recorded yet.</p>
            ) : (
              <div className="space-y-2">
                {payoutHistory.map(ph => (
                  <div key={ph.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <div>
                      <span className="text-sm font-semibold text-green-600">{ph.amount.toFixed(2)} JOD</span>
                      {ph.note && <p className="text-xs text-gray-500 mt-0.5">{ph.note}</p>}
                    </div>
                    <span className="text-xs text-gray-400">{new Date(ph.paid_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── HELPER COMPONENTS ───────────────────────────────────────────

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    emerald: 'bg-emerald-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
    amber: 'bg-amber-500',
    purple: 'bg-purple-500',
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate">{title}</p>
          <p className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <div className={`p-2 rounded-lg ${colorClasses[color] || colorClasses.blue}`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────

const AdminFinance = ({ period = 'all', customStart = '', customEnd = '' }) => {
  const [subTab, setSubTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [salesPersons, setSalesPersons] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query string with date range
      let queryString = '';
      if (period === 'custom' && customStart && customEnd) {
        queryString = `?start_date=${customStart}&end_date=${customEnd}`;
      } else {
        queryString = `?period=${period}`;
      }
      
      const [summaryData, spData] = await Promise.all([
        apiClient.get(`/api/auth/admin/finance/summary/${queryString}`),
        apiClient.get('/api/auth/admin/finance/sales-persons/'),
      ]);
      setSummary(summaryData);
      setSalesPersons(spData);
    } catch (err) {
      console.error('Failed to fetch finance data:', err);
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-700/50 rounded-lg p-1 w-fit">
        {SUB_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              subTab === tab.id
                ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {subTab === 'overview' && <FinanceOverview summary={summary} loading={loading} onRefresh={fetchData} />}
      {subTab === 'purchases' && <PurchasesList salesPersons={salesPersons} />}
      {subTab === 'expenses' && <ExpensesList onRefresh={fetchData} />}
      {subTab === 'sales-team' && <SalesTeam salesPersons={salesPersons} onRefresh={fetchData} />}
    </div>
  );
};

export default AdminFinance;
